import { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import {
  type AnalyticalVariableListItem,
  useAnalyticalVariableListQuery,
} from '@verantos-dev/feature-analytical-variables';
import type { ApiWorkflowDialogMessages } from '@verantos-dev/shared-components';

import { selectVariables } from '../state/selectors';
import { useAnalyticalDatasetDefinitionStore } from '../state/useAnalyticalDatasetDefinitionStore';
import type { UseAddVariablePickerModalArgs, UseAddVariablePickerModalResult } from '../types';

export const useAddVariablePickerModal = ({
  projectId,
  onClose,
}: UseAddVariablePickerModalArgs): UseAddVariablePickerModalResult => {
  const intl = useIntl();

  const variables = useAnalyticalDatasetDefinitionStore(selectVariables);
  const addVariable = useAnalyticalDatasetDefinitionStore((s) => s.addVariable);

  const {
    data: listResponse,
    isLoading,
    isFetching,
    isError,
  } = useAnalyticalVariableListQuery(undefined, projectId);

  // Show every AV the library returns regardless of kind. SQL-first (`av_denovo`)
  // AVs carry `jsonValue: null`, so the old `jsonValue.logic` presence filter
  // silently dropped them — leaving only structured (`av_ir`) variables. The
  // dataset only needs the avId, so no kind restriction is warranted.
  const allItems = useMemo<AnalyticalVariableListItem[]>(
    () => listResponse?.data ?? [],
    [listResponse]
  );

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedAvIds, setSelectedAvIds] = useState<Set<string>>(new Set());

  const alreadyAddedAvIds = useMemo(() => new Set(variables.map((v) => v.avId)), [variables]);

  const filteredItems = useMemo<AnalyticalVariableListItem[]>(() => {
    const q = searchQuery.trim().toLowerCase();
    // OR-6638: a variable declared row_level="event" returns one row per
    // qualifying event, not one value per patient — never eligible as an AD
    // column.
    const eligibleItems = allItems.filter(
      (item) => Boolean(item.sqlBody) && item.rowLevel !== 'event'
    );
    if (!q) return eligibleItems;
    return eligibleItems.filter(
      (item) =>
        item.name.toLowerCase().includes(q) || (item.description ?? '').toLowerCase().includes(q)
    );
  }, [allItems, searchQuery]);

  const onSearchChange = useCallback((next: string) => setSearchQuery(next), []);

  const onSelectAv = useCallback((avId: string) => {
    setSelectedAvIds((prev) => {
      const next = new Set(prev);
      if (next.has(avId)) next.delete(avId);
      else next.add(avId);
      return next;
    });
  }, []);

  // Bulk select/clear from the list's Select all toggle: the list hands us the
  // full set of selectable ids (or an empty array to clear).
  const onSelectAll = useCallback((avIds: string[]) => setSelectedAvIds(new Set(avIds)), []);

  const isSelected = useCallback((avId: string) => selectedAvIds.has(avId), [selectedAvIds]);

  const onConfirm = useCallback(() => {
    if (selectedAvIds.size === 0) return;
    selectedAvIds.forEach((avId) => addVariable({ avId }));
    onClose();
  }, [selectedAvIds, addVariable, onClose]);

  const onCancelClose = useCallback(() => onClose(), [onClose]);

  const canConfirm = selectedAvIds.size > 0 && !isLoading;

  const dialogMessages: ApiWorkflowDialogMessages = {
    entrySaveButtonText: intl.formatMessage(
      { id: 'analyticalDatasetDetail.addVariableModal.confirm' },
      { count: selectedAvIds.size }
    ),
    entryCancelButtonText: intl.formatMessage({
      id: 'analyticalDatasetDetail.addVariableModal.cancel',
    }),
  };

  return {
    title: intl.formatMessage({ id: 'analyticalDatasetDetail.addVariableModal.title' }),
    items: filteredItems,
    searchQuery,
    onSearchChange,
    onSelectAv,
    onSelectAll,
    isSelected,
    alreadyAddedAvIds,
    isLoading: isLoading || isFetching,
    isError,
    onConfirm,
    onCancelClose,
    canConfirm,
    dialogMessages,
  };
};
